import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import {
  fetchMinistryMembers,
  fetchProjectCatalogs,
  fetchSpaceConflictPreview,
  fetchWritableMinistries,
} from '../api/projects.api';
import { queryKeys } from '@/shared/lib/queryKeys';
import type {
  Id,
  MinistryMember,
  ProjectCatalogs,
  SpaceConflictPreview,
  WritableMinistry,
} from '@/shared/types/domain';

/**
 * O vocabulário do formulário: espaços, categorias, tipos de apoio e origens.
 *
 * `staleTime` longo porque catálogo muda quando a secretaria cadastra alguma
 * coisa — o que acontece algumas vezes por ano, não a cada digitação.
 */
export function useProjectCatalogs(): UseQueryResult<ProjectCatalogs> {
  return useQuery({
    queryKey: queryKeys.catalogs,
    queryFn: fetchProjectCatalogs,
    staleTime: 10 * 60_000,
  });
}

/**
 * Ministérios em que a pessoa pode ESCREVER (US-08) — não é o mesmo que os que
 * ela enxerga, e a distância entre os dois é o que gateia a porta do modo
 * ajuste (spec-project-adjustments.md §3.1).
 *
 * `enabled` opcional para quem só precisa da lista em um caso: a tela de
 * detalhe a consulta apenas com o projeto devolvido, e não a cada projeto
 * aberto. Quem sempre precisa — o formulário, `/rascunhos` — chama sem
 * argumento e nada muda.
 */
export function useWritableMinistries(enabled = true): UseQueryResult<WritableMinistry[]> {
  return useQuery({
    queryKey: queryKeys.ministries.writable,
    queryFn: fetchWritableMinistries,
    enabled,
    staleTime: 5 * 60_000,
  });
}

/** Quem pode ser responsável, dentro do ministério escolhido (§5.4). */
export function useMinistryMembers(ministryId: Id | null): UseQueryResult<MinistryMember[]> {
  return useQuery({
    queryKey: queryKeys.ministries.members(ministryId ?? ''),
    queryFn: () => fetchMinistryMembers(ministryId as Id),
    enabled: Boolean(ministryId),
    staleTime: 5 * 60_000,
  });
}

/**
 * O alerta de conflito ao vivo da seção 3 (decisão 15).
 *
 * Só consulta com espaço E janela completos — sem os dois não existe pergunta
 * a fazer, e disparar assim mesmo encheria a rede de chamadas que voltam
 * vazias enquanto a pessoa ainda está escolhendo a data.
 *
 * `staleTime` curto: a resposta muda quando OUTRO ministério tem projeto
 * aprovado, e é justamente essa novidade que o alerta existe para mostrar.
 */
export function useSpaceConflictPreview(
  spaceIds: readonly Id[],
  startsAt: string | null,
  endsAt: string | null,
  excludeProjectId?: Id,
): UseQueryResult<SpaceConflictPreview[]> {
  const enabled = spaceIds.length > 0 && Boolean(startsAt) && Boolean(endsAt);

  return useQuery({
    queryKey: queryKeys.spaceConflictPreview(spaceIds, startsAt, endsAt, excludeProjectId),
    queryFn: () =>
      fetchSpaceConflictPreview(spaceIds, startsAt as string, endsAt as string, excludeProjectId),
    enabled,
    staleTime: 30_000,
  });
}
